/**
 * Zusammenbau der Anwendung: Serverzustand, Kurzmeldungen, Routen.
 *
 * Zusätzlich hängen hier zwei Dinge, die zum Betrieb ohne Netz gehören:
 * der Start der Ausgangswarteschlange und der Hinweis auf eine neue Fassung des
 * Dienstarbeiters.
 */
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

import { subscribeSession } from "@/api/client";
import { createQueryClient } from "@/api/queryClient";
import { Button } from "@/components/ui/Button";
import { ToastProvider } from "@/components/ui/Toast";
import { t } from "@/i18n";
import { pruneConfirmed, startQueue } from "@/lib/queue";
import { requestPersistentStorage } from "@/platform/storage";
import { router } from "@/router";

/**
 * Hinweis auf eine neue Fassung.
 *
 * Kein selbsttätiges Neuladen: Eine noch nicht gespeicherte Meldung wäre danach
 * verloren. Der Nutzer entscheidet, wann der Wechsel passt.
 */
function UpdatePrompt() {
  const { needRefresh, updateServiceWorker } = useRegisterSW({ immediate: true });
  const [dismissed, setDismissed] = useState(false);
  const [needsRefresh] = needRefresh;

  if (!needsRefresh || dismissed) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-[calc(4.5rem+var(--safe-b))] z-[70] mx-auto flex max-w-md items-center gap-3 rounded-card border border-line-strong bg-surface-raised px-3 py-3 shadow-raised md:bottom-4"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[0.9375rem] font-semibold text-ink">{t.pwa.updateTitle}</p>
        <p className="text-[0.875rem] text-ink-muted">{t.pwa.updateBody}</p>
      </div>
      <Button variant="ghost" onClick={() => setDismissed(true)}>
        {t.pwa.updateLater}
      </Button>
      <Button variant="primary" onClick={() => void updateServiceWorker(true)}>
        {t.pwa.updateAction}
      </Button>
    </div>
  );
}

export function AppProviders() {
  const queryClient = useMemo(() => createQueryClient(), []);

  useEffect(() => {
    // Dauerhaften Speicher anfordern: Ohne das darf der Browser die Datenbank
    // bei Platznot löschen — mit noch nicht übertragenen Meldungen darin.
    void requestPersistentStorage();
    const stopQueue = startQueue();
    // Bestätigte Anhänge nach einem Tag abräumen. Ein übertragenes Foto belegt
    // sonst weiter Platz, der für die nächste Meldung fehlt.
    void pruneConfirmed();
    return stopQueue;
  }, []);

  useEffect(() => {
    // Nach dem Abmelden ist der zwischengespeicherte Serverzustand fremder
    // Besitz — er darf beim nächsten Anmelden nicht wieder auftauchen.
    return subscribeSession((session) => {
      if (session === null) queryClient.clear();
      void router.invalidate();
    });
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
        <UpdatePrompt />
      </ToastProvider>
    </QueryClientProvider>
  );
}
